/**
 * Search Tool - Knowledge Base Search (Vodafone Showcase)
 * 
 * This tool searches a dummy knowledge base for relevant information.
 * In a production scenario, this would connect to a real search service
 * like Azure AI Search, Elasticsearch, or a vector database.
 * 
 * NOTE: This tool is for the Vodafone showcase and is NOT registered by default.
 * It's kept in the repository for future use.
 */

import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';

// Dummy knowledge base data (similar to VoiceRAG implementation)
const KNOWLEDGE_BASE: Record<string, { title: string; content: string }> = {
  'vodafone_5g': {
    title: 'Vodafone 5G Network Coverage',
    content:
      'Vodafone 5G is available in major cities across Germany, providing ultra-fast mobile internet speeds up to 1 Gbps. The network covers metropolitan areas and is continuously expanding to rural regions. 5G technology enables new use cases like IoT, autonomous vehicles, and enhanced mobile broadband.'
  },
  'vodafone_tariffs': {
    title: 'Vodafone Mobile Tariffs',
    content:
      'Vodafone offers various mobile tariffs including Red, Young, and GigaMobil plans. All plans include unlimited calls and SMS within Germany. Data volumes range from 5GB to unlimited, with speeds up to 500 Mbps on 5G. EU roaming is included in all tariffs at no extra cost.'
  },
  'vodafone_iot': {
    title: 'Vodafone IoT Solutions',
    content:
      'Vodafone provides comprehensive IoT solutions for businesses, including global connectivity, device management, and analytics platforms. Our IoT services support smart cities, connected vehicles, industrial automation, and agriculture. We offer flexible data plans and dedicated support for enterprise deployments.'
  },
  'vodafone_customer_service': {
    title: 'Vodafone Customer Service',
    content:
      'Vodafone customer service is available 24/7 through multiple channels: phone hotline, online chat, mobile app, and retail stores. Premium customers enjoy priority support and dedicated account managers. Self-service options include online account management, bill payment, and troubleshooting guides.'
  },
  'vodafone_business': {
    title: 'Vodafone Business Solutions',
    content:
      'Vodafone Business offers tailored solutions for companies of all sizes, including mobile contracts with volume discounts, unified communications, cloud services, and cybersecurity. We provide dedicated business support, flexible contract terms, and integration with existing enterprise systems.'
  },
  'vodafone_network_quality': {
    title: 'Vodafone Network Quality',
    content:
      'Vodafone network has been rated as one of the best in Germany by independent tests. Our 4G LTE network covers 99% of the population with average speeds of 50-100 Mbps. 5G is available in over 150 cities. We continuously invest in network infrastructure to ensure reliability and performance.'
  },
  'vodafone_roaming': {
    title: 'Vodafone Roaming Services',
    content:
      'Vodafone customers can use their mobile plan in the entire EU at no extra cost. Outside the EU, we offer travel data passes for affordable roaming in over 200 countries. Our roaming app helps you track usage and costs. Premium plans include additional international benefits.'
  },
  'vodafone_digital_services': {
    title: 'Vodafone Digital Services',
    content:
      'Vodafone offers various digital services including streaming partnerships (GigaTV, music services), cloud storage, mobile security, and device insurance. Our digital ecosystem integrates entertainment, productivity, and security services into a seamless mobile experience.'
  }
};

/**
 * Simple keyword-based search through the knowledge base
 */
function simpleSearch(query: string): Array<{ id: string; title: string; content: string }> {
  const queryLower = query.toLowerCase();
  const results: Array<{ id: string; title: string; content: string; score: number }> = [];

  for (const [id, data] of Object.entries(KNOWLEDGE_BASE)) {
    let score = 0;

    // Check if query appears in title or content
    if (data.title.toLowerCase().includes(queryLower)) {
      score += 10;
    }
    if (data.content.toLowerCase().includes(queryLower)) {
      score += 5;
    }

    // Check individual words
    const words = queryLower.split(/\s+/);
    for (const word of words) {
      if (word.length > 2) {
        // Ignore very short words
        if (data.title.toLowerCase().includes(word)) {
          score += 2;
        }
        if (data.content.toLowerCase().includes(word)) {
          score += 1;
        }
      }
    }

    if (score > 0) {
      results.push({ id, title: data.title, content: data.content, score });
    }
  }

  // Sort by relevance score
  results.sort((a, b) => b.score - a.score);

  // Return top 5 results
  return results.slice(0, 5).map(({ id, title, content }) => ({ id, title, content }));
}

/**
 * Tool schema for the search tool
 */
export const searchToolVodafoneSchema: ToolSchema = {
  type: 'function',
  name: 'search',
  description:
    'Search the knowledge base. The knowledge base contains information about Vodafone services, ' +
    'tariffs, network coverage, and customer service. Results are formatted as a source name first in ' +
    'square brackets, followed by the text content, and a line with "-----" at the end of each result.',
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query to find relevant information in the knowledge base'
      }
    },
    required: ['query'],
    additionalProperties: false
  }
};

/**
 * Tool implementation for the search tool
 */
export const searchToolVodafoneImplementation: ToolImplementation = async (args: any): Promise<ToolResult> => {
  try {
    const { query } = args;

    if (!query || typeof query !== 'string') {
      return {
        success: false,
        error: 'Invalid query parameter. Expected a non-empty string.'
      };
    }

    console.log(`🔍 Searching Vodafone knowledge base for: "${query}"`);

    const results = simpleSearch(query);

    if (results.length === 0) {
      return {
        success: true,
        data: 'No results found for your query. Please try different search terms.'
      };
    }

    // Format results in the expected format (similar to VoiceRAG)
    let formattedResults = '';
    for (const result of results) {
      formattedResults += `[${result.id}]: ${result.content}\n-----\n`;
    }

    console.log(`✅ Found ${results.length} results`);

    return {
      success: true,
      data: formattedResults
    };
  } catch (error) {
    console.error('❌ Search tool error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred during search'
    };
  }
};

